import { MatchInfo, Player } from '../types';
import { formatRoundType } from './formatters';

interface TranslationOptions {
  params?: Record<string, string | number>;
  fallback?: string;
}

type TranslationFunction = (key: string, options?: TranslationOptions) => string;

/**
 * Statut de balle spéciale (game ball, match ball, 10 all)
 */
export type BallStatus = 'game_ball' | 'match_ball' | 'ten_all' | null;

/**
 * Calcule le statut de balle actuel
 * @param serverScore Score du serveur
 * @param receiverScore Score du receveur
 * @param serverSetScore Nombre de jeux gagnés par le serveur
 * @param receiverSetScore Nombre de jeux gagnés par le receveur
 * @param setsToWin Nombre de jeux nécessaires pour gagner le match (2 pour BestOf3, 3 pour BestOf5)
 */
export function getBallStatus(
  serverScore: number,
  receiverScore: number,
  serverSetScore: number,
  setsToWin: number,
  receiverSetScore?: number
): BallStatus {
  // 10 all uniquement (pas 11-11, 12-12, etc.) : annonce spéciale "2 points d'écart"
  if (serverScore === 10 && receiverScore === 10) {
    return 'ten_all';
  }

  // Déterminer qui mène et s'il est en position de gagner
  const highScore = Math.max(serverScore, receiverScore);
  const lowScore = Math.min(serverScore, receiverScore);

  // Game ball : un joueur a >= 10 et mène d'au moins 1 point
  const isGameBall = highScore >= 10 && highScore > lowScore;

  if (isGameBall) {
    // Déterminer le setScore du joueur qui mène
    const leaderSetScore = serverScore > receiverScore
      ? serverSetScore
      : (receiverSetScore ?? 0);

    // Match ball : si gagner ce jeu = gagner le match
    const wouldWinMatch = (leaderSetScore + 1) >= setsToWin;
    return wouldWinMatch ? 'match_ball' : 'game_ball';
  }

  return null;
}

interface MatchStartParams {
  matchInfo: MatchInfo;
  servingPlayer: Player;
  t: TranslationFunction;
}

/**
 * Génère les lignes d'annonce pour le début de match
 *
 * Format (max 4 lignes):
 * - Ligne 1 (optionnelle): {competition}. (nom de la compétition)
 * - Ligne 2 (optionnelle): Playing on court {court}, {round}. (ou juste court, ou juste round)
 * - Ligne 3 (optionnelle): Match {playerA} vs {playerB}
 * - Ligne 4 (obligatoire): {server} to serve, {receiver} to receive, best of {numGames} games, love-all.
 */
/**
 * Vérifie si une valeur est considérée comme "vide" ou invalide
 */
function isEmptyValue(value: string | undefined | null): boolean {
  if (!value) return true;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return true;
  // Valeurs considérées comme vides/invalides
  const invalidValues = ['undefined', 'null', 'simple match', 'court undefined', 'no competition'];
  return invalidValues.includes(trimmed);
}

export function generateMatchStartAnnouncement({
  matchInfo,
  servingPlayer,
  t,
}: MatchStartParams): string[] {
  const lines: string[] = [];

  const hasCompetition = !isEmptyValue(matchInfo.competitionName);
  const hasCourt = !isEmptyValue(matchInfo.courtName);
  const hasRound = !isEmptyValue(matchInfo.roundType);

  // Ligne 1 (optionnelle): Compétition
  if (hasCompetition) {
    lines.push(
      t('announcements.match_start.competition_only', {
        params: { competition: matchInfo.competitionName },
      })
    );
  }

  // Ligne 2 (optionnelle): Court et/ou round
  // roundType peut être un code tournoi MySquash (DF, F, QF...) : on le localise
  // pour éviter d'annoncer un code brut à l'oral.
  const roundLabel = formatRoundType(t, matchInfo.roundType);
  if (hasCourt && hasRound) {
    lines.push(
      t('announcements.match_start.court_and_round', {
        params: { court: matchInfo.courtName, round: roundLabel },
      })
    );
  } else if (hasCourt) {
    lines.push(
      t('announcements.match_start.court_only', {
        params: { court: matchInfo.courtName },
      })
    );
  } else if (hasRound) {
    lines.push(
      t('announcements.match_start.round_only', {
        params: { round: roundLabel },
      })
    );
  }

  // Ligne 3: Match playerA vs playerB (joueurs obligatoires)
  lines.push(
    t('announcements.match_start.players', {
      params: { playerA: matchInfo.playerA, playerB: matchInfo.playerB },
    })
  );

  // Ligne 4: Serveur, receveur, format
  const serverName = servingPlayer === 'A' ? matchInfo.playerA : matchInfo.playerB;
  const receiverName = servingPlayer === 'A' ? matchInfo.playerB : matchInfo.playerA;
  const numGames = matchInfo.scoringType === 'BestOf5' ? 5 : 3;

  lines.push(
    t('announcements.match_start.serve', {
      params: { server: serverName, receiver: receiverName, numGames },
    })
  );

  return lines;
}

interface ScoreUpdateParams {
  serverScore: number;
  receiverScore: number;
  ballStatus?: BallStatus;
  t: TranslationFunction;
}

/**
 * Convertit un score numérique en texte d'annonce
 * En anglais: 0 = "Love", sinon le nombre
 * En français: toujours le nombre
 */
function formatScore(score: number, t: TranslationFunction): string {
  if (score === 0) {
    return t('announcements.score_update.love', { fallback: '0' });
  }
  return String(score);
}

/**
 * Génère l'annonce du score quand le serveur marque (pas de changement de service)
 *
 * Format: "{serverScore} - {receiverScore}" ou "{score} all" si égalité
 * Avec ballStatus: "{serverScore} - {receiverScore}, game ball" ou "10 all"
 * En anglais: "Love" remplace 0
 */
export function generateScoreAnnouncement({
  serverScore,
  receiverScore,
  ballStatus,
  t,
}: ScoreUpdateParams): string[] {
  // Cas spécial 10 all (avec message "2 points d'écart")
  if (ballStatus === 'ten_all') {
    return [t('announcements.score_update.ten_all')];
  }

  let message: string;

  // Égalité : utiliser "X all" / "X partout"
  if (serverScore === receiverScore) {
    message = t('announcements.score_update.score_all', {
      params: { score: serverScore },
    });
  } else {
    const serverText = formatScore(serverScore, t);
    const receiverText = formatScore(receiverScore, t);
    message = t('announcements.score_update.score', {
      params: { serverScore: serverText, receiverScore: receiverText },
    });
  }

  // Ajouter game ball / match ball
  if (ballStatus === 'game_ball') {
    message += ', ' + t('announcements.score_update.game_ball');
  } else if (ballStatus === 'match_ball') {
    message += ', ' + t('announcements.score_update.match_ball');
  }

  return [message];
}

interface HandoutParams {
  newServerScore: number;
  newReceiverScore: number;
  ballStatus?: BallStatus;
  t: TranslationFunction;
}

/**
 * Génère l'annonce de changement de service (hand out)
 * Quand le receveur marque, il devient le nouveau serveur
 *
 * Format: "Hand out, {newServerScore} - {newReceiverScore}" ou "Hand out, {score} all" si égalité
 * Avec ballStatus: "Hand out, {newServerScore} - {newReceiverScore}, game ball" ou "Hand out, 10 all"
 * Le score du nouveau serveur est annoncé en premier
 */
export function generateHandoutAnnouncement({
  newServerScore,
  newReceiverScore,
  ballStatus,
  t,
}: HandoutParams): string[] {
  // Cas spécial 10 all (avec message "2 points d'écart")
  if (ballStatus === 'ten_all') {
    return [t('announcements.handout.ten_all')];
  }

  // Choisir le bon template selon le ballStatus
  if (ballStatus === 'game_ball') {
    const serverText = formatScore(newServerScore, t);
    const receiverText = formatScore(newReceiverScore, t);
    return [
      t('announcements.handout.with_game_ball', {
        params: { newServerScore: serverText, newReceiverScore: receiverText },
      }),
    ];
  } else if (ballStatus === 'match_ball') {
    const serverText = formatScore(newServerScore, t);
    const receiverText = formatScore(newReceiverScore, t);
    return [
      t('announcements.handout.with_match_ball', {
        params: { newServerScore: serverText, newReceiverScore: receiverText },
      }),
    ];
  }

  // Égalité : utiliser "X all" / "X partout"
  if (newServerScore === newReceiverScore) {
    return [
      t('announcements.handout.message_all', {
        params: { score: newServerScore },
      }),
    ];
  }

  const serverText = formatScore(newServerScore, t);
  const receiverText = formatScore(newReceiverScore, t);
  return [
    t('announcements.handout.message', {
      params: { newServerScore: serverText, newReceiverScore: receiverText },
    }),
  ];
}

interface GameEndParams {
  winnerName: string;
  loserName: string;
  winnerScore: number;
  loserScore: number;
  winnerGames: number;
  loserGames: number;
  t: TranslationFunction;
}

interface GameStartParams {
  setScoreA: number;
  setScoreB: number;
  playerAName: string;
  playerBName: string;
  serverName: string;
  t: TranslationFunction;
}

interface GameResult {
  winner: 'A' | 'B';
  scoreA: number;
  scoreB: number;
}

interface MatchEndParams {
  winnerName: string;
  winnerGames: number;
  loserGames: number;
  gameResults: GameResult[];
  winner: 'A' | 'B';
  t: TranslationFunction;
}

/**
 * Génère l'annonce de fin de jeu
 *
 * Format (2 lignes):
 * - Ligne 1: "Game to {winner}, {winnerScore} - {loserScore}"
 * - Ligne 2: "{winner} leads {winnerGames} - {loserGames}" ou "{games} all" si égalité
 */
export function generateGameEndAnnouncement({
  winnerName,
  loserName,
  winnerScore,
  loserScore,
  winnerGames,
  loserGames,
  t,
}: GameEndParams): string[] {
  const lines: string[] = [];

  // Ligne 1: "{winnerScore} - {loserScore}, Game to {winner}"
  // Utiliser formatScore pour convertir 0 en "Love" en anglais
  const winnerScoreText = formatScore(winnerScore, t);
  const loserScoreText = formatScore(loserScore, t);
  const scoreLine = t('announcements.game_end.score', {
    params: { winnerScore: winnerScoreText, loserScore: loserScoreText },
  });
  const gameToLine = t('announcements.game_end.game_to', {
    params: { winner: winnerName },
  });
  lines.push(`${scoreLine}, ${gameToLine}`);

  // Ligne 2: "{leader} leads X game(s) to Y" ou "{games} all" si égalité
  // Note: le gagnant du jeu n'est pas forcément celui qui mène au score en jeux
  // Utiliser formatScore pour convertir 0 en "Love" en anglais
  if (winnerGames === loserGames) {
    lines.push(
      t('announcements.game_end.tied', {
        params: { winnerGames },
      })
    );
  } else {
    // Déterminer qui mène vraiment (le gagnant du jeu ou son adversaire)
    const leaderGames = Math.max(winnerGames, loserGames);
    const trailerGames = Math.min(winnerGames, loserGames);
    // Le leader est le gagnant du jeu si winnerGames > loserGames, sinon c'est le perdant
    const leaderName = winnerGames > loserGames ? winnerName : loserName;

    const leaderGamesText = formatScore(leaderGames, t);
    const trailerGamesText = formatScore(trailerGames, t);
    // Utiliser leads_one (1 game) ou leads_other (2+ games)
    const leadsKey = leaderGames === 1
      ? 'announcements.game_end.leads_one'
      : 'announcements.game_end.leads_other';
    lines.push(
      t(leadsKey, {
        params: {
          winner: leaderName,
          winnerGames: leaderGamesText,
          loserGames: trailerGamesText,
        },
      })
    );
  }

  return lines;
}

/**
 * Génère l'annonce de début de jeu (après le premier jeu)
 *
 * Format (2 lignes):
 * - Ligne 1: "{leader} leads {leaderGames} game(s) to {trailerGames}" ou "{games} all" si égalité
 * - Ligne 2: "{server} to serve. Love all."
 */
export function generateGameStartAnnouncement({
  setScoreA,
  setScoreB,
  playerAName,
  playerBName,
  serverName,
  t,
}: GameStartParams): string[] {
  const lines: string[] = [];

  // Ligne 1: Score en jeux
  if (setScoreA === setScoreB) {
    lines.push(
      t('announcements.game_start.tied', {
        params: { games: setScoreA },
      })
    );
  } else {
    // Déterminer le leader et le trailer
    const leaderGames = Math.max(setScoreA, setScoreB);
    const trailerGames = Math.min(setScoreA, setScoreB);
    const leaderName = setScoreA > setScoreB ? playerAName : playerBName;

    // Utiliser formatScore pour convertir 0 en "Love" en anglais
    const trailerGamesText = formatScore(trailerGames, t);

    // Utiliser leads_one (1 game) ou leads_other (2+ games)
    const leadsKey = leaderGames === 1
      ? 'announcements.game_start.leads_one'
      : 'announcements.game_start.leads_other';

    lines.push(
      t(leadsKey, {
        params: {
          leader: leaderName,
          leaderGames,
          trailerGames: trailerGamesText,
        },
      })
    );
  }

  // Ligne 2: Serveur (seulement si pas évident)
  // Règle WSF: quand le leader est aussi le serveur (il vient de gagner le jeu),
  // on n'annonce pas le serveur. On l'annonce seulement en cas d'égalité ou si
  // le serveur n'est pas le leader.
  const leaderName = setScoreA > setScoreB ? playerAName
    : setScoreB > setScoreA ? playerBName
    : null;

  if (!leaderName || serverName !== leaderName) {
    lines.push(
      t('announcements.game_start.serve', {
        params: { server: serverName },
      })
    );
  } else {
    lines.push(t('announcements.game_start.love_all'));
  }

  return lines;
}

/**
 * Génère l'annonce de fin de match
 *
 * Format (1 ligne):
 * "{lastGameScore}, Match to {winner}, {winnerGames} games to {loserGames}, {allScores}"
 *
 * Exemple: "11-7, Match to Smith, 3 games to 1, 11-9, 9-11, 11-5, 11-7"
 * En anglais: 0 = "Love" (ex: "11-Love")
 */
export function generateMatchEndAnnouncement({
  winnerName,
  winnerGames,
  loserGames,
  gameResults,
  winner,
  t,
}: MatchEndParams): string[] {
  // Dernier jeu - score du vainqueur en premier
  const lastGame = gameResults[gameResults.length - 1];
  const lastGameWinnerScore = winner === 'A' ? lastGame.scoreA : lastGame.scoreB;
  const lastGameLoserScore = winner === 'A' ? lastGame.scoreB : lastGame.scoreA;
  const lastGameWinnerText = formatScore(lastGameWinnerScore, t);
  const lastGameLoserText = formatScore(lastGameLoserScore, t);
  const lastGameScore = `${lastGameWinnerText}-${lastGameLoserText}`;

  // Tous les scores - score du vainqueur du match en premier pour chaque jeu
  // Utiliser formatScore pour convertir 0 en "Love" en anglais
  const allScores = gameResults
    .map((game) => {
      const winnerScore = winner === 'A' ? game.scoreA : game.scoreB;
      const loserScore = winner === 'A' ? game.scoreB : game.scoreA;
      const winnerText = formatScore(winnerScore, t);
      const loserText = formatScore(loserScore, t);
      return `${winnerText}-${loserText}`;
    })
    .join(', ');

  // Utiliser formatScore pour le nombre de jeux perdus (0 = "Love" en anglais)
  const loserGamesText = formatScore(loserGames, t);

  const message = t('announcements.match_end.message', {
    params: {
      lastGameScore,
      winner: winnerName,
      winnerGames,
      loserGames: loserGamesText,
      allScores,
    },
  });

  return [message];
}
